import type { IgAccountOverview } from "@gf/shared";
import { Badge, Button, Callout, ProgressBar, Skeleton, useToast } from "@gf/ui";
import { useEffect, useMemo } from "react";
import { useLocation } from "react-router";
import { AddIgAccountGuide, ADD_ACCOUNT_ANCHOR } from "../../components/accounts/AddIgAccountGuide";
import { IgAccountCard } from "../../components/accounts/IgAccountCard";
import { InboxPrivacyCard } from "../../components/InboxPrivacyCard";
import { NoIgAccount } from "../../components/NoIgAccount";
import { isApiError } from "../../lib/api";
import { useUser } from "../../lib/auth";
import { useIgAccount, useIgAccountsOverview } from "../../lib/igAccount";

/** Settings → Instagram accounts: every connected account (workspace), plan usage, how to add one. */
export function InstagramTab() {
  const user = useUser();
  const toast = useToast();
  const { accounts, isLoading, current, setCurrent } = useIgAccount();
  const overview = useIgAccountsOverview({ enabled: accounts.length > 0 });
  const { hash } = useLocation();

  const byId = useMemo(() => new Map<string, IgAccountOverview>((overview.data?.items ?? []).map((o) => [o.account.id, o])), [overview.data]);

  // "Add an account" in the switcher links to #add-account.
  useEffect(() => {
    if (isLoading || hash !== `#${ADD_ACCOUNT_ANCHOR}`) return;
    document.getElementById(ADD_ACCOUNT_ANCHOR)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [hash, isLoading]);

  if (isLoading) {
    return (
      <div className="gf-stack">
        <Skeleton height={140} radius={10} />
        <Skeleton height={140} radius={10} />
      </div>
    );
  }
  if (accounts.length === 0) {
    return (
      <div className="gf-stack">
        <NoIgAccount compact />
        <InboxPrivacyCard />
      </div>
    );
  }

  const limit = overview.data?.limit ?? user.igAccountLimit;
  const used = overview.data?.used ?? accounts.length;
  const unlimited = user.plan === "lifetime";
  const atLimit = !unlimited && used >= limit;
  // An older API without /overview answers 404: show the accounts without today's numbers.
  const overviewFailed = overview.isError && !isApiError(overview.error, 404);

  return (
    <div className="gf-stack">
      <div className="gfp-ws-usage">
        {unlimited ? <p><strong>{used} connected Instagram accounts · Unlimited included</strong></p> : <ProgressBar
          value={Math.min(used, limit)}
          max={Math.max(limit, 1)}
          size="sm"
          label={
            <span>
              <b className="gfp-strong">
                {used} of {limit}
              </b>{" "}
              Instagram {limit === 1 ? "account" : "accounts"} on your plan
            </span>
          }
          valueLabel={
            atLimit ? (
              <a href={`#${ADD_ACCOUNT_ANCHOR}`} className="gfp-ws-usage__limit">
                <Badge size="sm" tone="warn">
                  At your limit
                </Badge>
              </a>
            ) : (
              `${limit - used} free`
            )
          }
        />}
        <p className="gf-muted gfp-ws-usage__hint">
          Every account is its own workspace: its own settings, brand voice, campaigns, contacts and history. Switch between
          them at the top of any page.
        </p>
      </div>

      {overviewFailed ? (
        <Callout tone="warn" title="Couldn't load today's numbers" action={<Button size="sm" onClick={() => void overview.refetch()}>Try again</Button>}>
          {overview.error instanceof Error ? overview.error.message : null}
        </Callout>
      ) : null}

      <ul className="gfp-iglist">
        {accounts.map((a) => {
          const o = byId.get(a.id) ?? null;
          return (
            <IgAccountCard
              key={a.id}
              // The overview row is fetched separately and may be fresher (driver, engine status).
              account={o?.account ?? a}
              overview={o}
              isCurrent={a.id === current?.id}
              onSwitch={() => {
                setCurrent(a.id);
                toast.success(`Switched to @${a.username}`, "Everything in GramFather now shows this account.");
              }}
            />
          );
        })}
      </ul>

      <AddIgAccountGuide used={used} limit={limit} />
      <InboxPrivacyCard />
    </div>
  );
}
